import { Component } from '@theme/component';

/**
 * A custom element that renders a video background.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  /** @type {IntersectionObserver | undefined} */
  #observer;

  connectedCallback() {
    super.connectedCallback();

    if ('IntersectionObserver' in window) {
      this.#observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.#loadVideo();
          this.#observer?.disconnect();
        },
        { rootMargin: '600px 0px' }
      );

      this.#observer.observe(this);
      return;
    }

    this.#loadVideo();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
  }

  #loadVideo() {
    if (this.hasAttribute('data-video-loading') || this.hasAttribute('data-video-loaded')) return;

    this.setAttribute('data-video-loading', 'true');

    const { videoSources, videoElement } = this.refs;

    const showVideo = () => {
      this.setAttribute('data-video-loaded', 'true');
    };

    videoElement.addEventListener('playing', showVideo, { once: true });
    videoElement.addEventListener('canplay', showVideo, { once: true });

    for (const source of videoSources) {
      const { videoSource } = source.dataset;

      if (videoSource) source.setAttribute('src', videoSource);
    }

    videoElement.load();

    if (videoElement.autoplay) {
      videoElement.play().catch(() => {
        // Some browsers can still block autoplay; keep the poster visible in that case.
      });
    }
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}
